import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';
import SearchBar from './SearchBar';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hfContent, setHfContent] = useState(null);
  const location = useLocation();

  const isGamingPage = location.pathname.startsWith('/gaming');
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    axios.get(`${API}/api/public/content/header_footer`).then(res => {
      if (res.data && Object.keys(res.data).length > 0) setHfContent(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getNavLabel = (key, fallback) => {
    const val = hfContent?.nav_labels?.[key];
    if (val && typeof val === 'object') return val[lang] || val.en || fallback;
    return val || fallback;
  };

  const navLinks = [
    { name: getNavLabel('home', t('nav.home')), path: '/' },
    { name: getNavLabel('gaming', t('nav.gaming')), path: '/gaming' },
    { name: getNavLabel('office', t('nav.office')), path: '/office' },
    { name: getNavLabel('support', t('nav.support')), path: '/support' },
    { name: getNavLabel('about', t('nav.about')), path: '/about' },
    { name: getNavLabel('contact', t('nav.contact')), path: '/contact' }
  ];

  const headerLogo = hfContent?.header_logo || "/viyero-logo.webp";

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'instant' });
  const getHeaderBg = () => {
    if (!isScrolled) return 'bg-transparent';
    if (isGamingPage) return 'bg-black/95 backdrop-blur-md shadow-sm';
    return 'bg-white/95 backdrop-blur-md shadow-sm';
  };

  const needsLightText = !isScrolled && isGamingPage;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getHeaderBg()}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" onClick={scrollTop} className={`flex items-center ${isGamingPage ? 'opacity-0 pointer-events-none' : ''}`}>
            <img src={headerLogo} alt="VIYERO" className="h-8 md:h-10 w-auto object-contain" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <nav className="flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={scrollTop}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? needsLightText || (isGamingPage && isScrolled)
                        ? 'text-white font-bold underline underline-offset-4'
                        : 'text-gray-900 font-bold border-b-2 border-gray-900 pb-1'
                      : needsLightText || (isGamingPage && isScrolled)
                        ? 'text-white/80 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <SearchBar />
            <LanguageSelector hfContent={hfContent} />
          </div>

          <Button variant="ghost" size="icon" aria-label="Menu" className={`md:hidden ${isGamingPage ? 'text-white hover:bg-white/10' : ''}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={() => { setMobileMenuOpen(false); scrollTop(); }}
              >
                {link.name}
              </Link>
            ))}
            <div className="px-3 py-2 flex items-center gap-2">
              <span className="text-sm text-gray-600 mr-2">Language:</span>
              <LanguageSelector hfContent={hfContent} />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
